import React from 'react';
import { Link } from 'react-router-dom';
import { BriefcaseIcon, DropletsIcon, InfinityIcon, GiftIcon, GlassesIcon, ZapIcon } from 'lucide-react';
import { includedGear, GearItem } from '../data/product';
import { useTrackOnView } from '../hooks/useTrackOnView';
const icons = [ZapIcon, GlassesIcon, BriefcaseIcon, DropletsIcon, InfinityIcon, GiftIcon];

/**
 * The one visual value stack: what physically shows up in the box. Product
 * renders drop into `includedGear[].image`; until the branded hard case and
 * anti-fog renders exist, those tiles show a neutral GritVue placeholder
 * rather than a generic stand-in product that is not ours.
 */
function GearVisual({
  item,
  Icon
}: {
  item: GearItem;
  Icon: React.ComponentType<{
    className?: string;
  }>;
}) {
  if (item.image) {
    return <div className="aspect-[4/3] w-full overflow-hidden border border-white/10 bg-ink-900" data-id="element-326" data-mp="hvefhpm7nhjg">
        <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" data-id="element-327" data-mp="mmedyzaza7o2" />
      </div>;
  }
  const isMystery = /mystery/i.test(item.name);
  return <div className={`flex aspect-[4/3] w-full items-center justify-center border ${isMystery ? 'border-grit-500/40 bg-ink-900' : 'border-white/10 bg-ink-900'}`} aria-hidden="true" data-id="element-328" data-mp="1p9qnvcj9hlr7">
      {isMystery ? <span className="font-display text-6xl font-extrabold leading-none text-grit-500" data-id="element-329" data-mp="1uaxvnblemkxv">?</span> : <Icon className="h-12 w-12 text-steel-500" data-id="element-330" data-mp="28z1730i7uou2" />}
    </div>;
}
export function IncludedGear() {
  const ref = useTrackOnView<HTMLElement>('mystery_gift_offer_viewed');
  return <section ref={ref} aria-labelledby="included-gear-heading" className="mt-10 border border-white/12 bg-ink-950 lg:mt-12" data-id="element-331" data-mp="2hzpjxrvvij5d">
      <div className="border-b border-white/10 px-5 py-5 sm:px-7" data-id="element-332" data-mp="2czut1oc3ltkb">
        <h3 id="included-gear-heading" className="grit-headline text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold uppercase text-offwhite" data-id="element-333" data-mp="nnizs3l04s0x">
          Look at everything in the box.
        </h3>
        <p className="mt-2 text-[15px] text-steel-400" data-id="element-334" data-mp="2is0gasrn90dk">
          The case and the anti-fog are not upsells. They ship with the order.
        </p>
      </div>

      <ul className="grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3" data-id="element-335" data-mp="2sn0r5pl9guk2">
        {includedGear.map((item, index) => {
        const Icon = icons[index] ?? GiftIcon;
        const isWarranty = /warranty/i.test(item.name);
        const body = <>
              <GearVisual item={item} Icon={Icon} data-id="element-336" data-mp="36nlcotik94h9" />
              <span className="mt-4 flex items-start justify-between gap-3" data-id="element-337" data-mp="36ma48ge678jt">
                <span className="font-display text-xl font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-338" data-mp="3bfmjw10vg50f">
                  {item.name}
                </span>
                <span className={`shrink-0 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${isWarranty ? 'gold-plate text-ink-950' : 'bg-grit-500 text-ink-950'}`} data-id="element-339" data-mp="3kfj9q5yzz9x2">
                  {item.tag}
                </span>
              </span>
              <span className="mt-1.5 block text-[14px] leading-relaxed text-steel-300" data-id="element-340" data-mp="18quy38zvd49i">{item.note}</span>
              <span className="mt-auto block pt-3 text-[11px] font-bold uppercase tracking-[0.16em] text-steel-500" data-id="element-341" data-mp="14r41uprkpvxl">
                {item.qty}
              </span>
            </>;
        return <li key={item.name} className="bg-ink-950" data-id="element-342" data-mp="1wgz39ksphc2m" data-mpi={index}>
              {isWarranty ? <Link to="/legal/guarantee" className="flex h-full flex-col p-5 transition-colors duration-150 ease-grit hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c] sm:p-6" data-id="element-343" data-mp="1sh6r6y43snsi">
                  {body}
                </Link> : <div className="flex h-full flex-col p-5 sm:p-6" data-id="element-344" data-mp="2g7awd9wxwvlm">{body}</div>}
            </li>;
      })}
      </ul>
    </section>;
}